import { authFetch } from 'auth/utils';
import {
  SimProfilesDTO,
  RSimSimProfilesResponseData,
  RSimInventoryResponsePagination,
} from './rSimApi.interface';
import { LAST_UPDATE_TIME_FILTERS, mapLastUpdateTimeToDateTime } from '../constants/lastUpdate';
import {
  RSimsData,
  RSimsLastPollingTimestampDTO,
  RSimsDTO,
  mapRSimsDTO,
  RSimFilters,
} from '../constants/rsim';
import { uppercaseFirstLetter } from 'common/utils/stringUtil';
import { envVariables } from 'src/common/utils/envVariables';
import {
  RSIM_ID_TYPE,
  RSimIdTypeName,
  RSimsFiltersRequestBody,
  RSimSort,
} from 'src/rSimInventory/constants/RSimProfile.model';
import { SIMULATED_OUTAGE_STATUS, SIMULATE_OUTAGE_FILTER } from '../constants/simulateOutage';
import {
  ACTIVE_PROFILE_FILTER,
  mapLastActiveProfileFilterToValue,
} from '../constants/activeProfile';

import { isEmpty, isEqual } from 'lodash';
import { fetchAssignedTagToRSimsProducts } from 'rSimInventory/RSimTags/services/tagsRSimProducts';
import { normilizeAssignedTagsToRSim } from 'tags/utils/mappers';
import { mapSimDTO } from 'src/common/components/Sim/Inventory/utils/simListResponseParsers';

import { getSearchIds } from '../Components/Inputs/RSimIdSearchInput.util';

export enum LastActiveProfile {
  Unknown,
  Primary,
  Secondary,
}

export const fetchRSim = async (euicc: string, signal?: AbortSignal): Promise<RSimsData> => {
  const urlQuery = new URLSearchParams();

  urlQuery.append('PageNumber', '1');
  urlQuery.append('PageSize', '1');
  urlQuery.append('Euicc', euicc);

  const rsimProfilesResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_API}?${urlQuery.toString()}`,
    { signal },
  );

  const rsimProfiles: RSimsDTO = await rsimProfilesResponse.json();

  const assignedTags = await fetchAssignedTagToRSimsProducts({
    euiccs: rsimProfiles.map((rsim) => rsim.euicc),
  });

  const normalizedTagsData = normilizeAssignedTagsToRSim(assignedTags);
  const items = mapRSimsDTO(rsimProfiles, { normalizedTagsData });

  return {
    items,
  };
};

const appendIdSerach = (
  body: RSimsFiltersRequestBody,
  searchText: string,
  chosenIdType: RSimIdTypeName,
) => {
  const searchIds = getSearchIds(searchText ?? '');

  if (searchIds.length === 1) {
    const id = chosenIdType === 'EID' ? 'euicc' : 'iccid';
    body[id] = searchIds[0];
  } else if (searchIds.length > 0 && searchIds[0]) {
    body['rsimProfileIdsFilter'] = {
      type: RSIM_ID_TYPE[chosenIdType],
      ids: searchIds,
    };
  }
};

const appendSimulatedOutage = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.simulateOutage) {
    if (searchParams.simulateOutage === SIMULATE_OUTAGE_FILTER.OFF) {
      body.simulatedOutageStatuses = [SIMULATED_OUTAGE_STATUS.NONE];
    } else if (searchParams.simulateOutage === SIMULATE_OUTAGE_FILTER.ON) {
      body.simulatedOutageStatuses = [
        SIMULATED_OUTAGE_STATUS.PRIMARY,
        SIMULATED_OUTAGE_STATUS.SECONDARY,
        SIMULATED_OUTAGE_STATUS.BOTH,
      ];
    }
  }
};

const appendEidRange = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.eidFrom && searchParams.eidTo) {
    body.rsimProfileIdsRange = {
      type: 1,
      idsFrom: searchParams.eidFrom,
      idsTo: searchParams.eidTo,
    };
  }
};

const appendLastActiveProfileRange = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (
    searchParams.lastActiveProfile &&
    searchParams.lastActiveProfile !== ACTIVE_PROFILE_FILTER.ANY
  ) {
    body.lastActiveProfile = mapLastActiveProfileFilterToValue(searchParams.lastActiveProfile);
  }
};

const appendAccounts = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.accounts && !isEqual(searchParams.accounts, [])) {
    const accountsList = searchParams.accounts;

    body.accountRef = accountsList.map((accountRef) => {
      return accountRef === null ? accountRef : Number(accountRef);
    });
  }
};

const appendLastUpdateTime = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (
    searchParams.lastUpdateTime &&
    searchParams.lastUpdateTime !== LAST_UPDATE_TIME_FILTERS.NONE
  ) {
    body.noPollingFrom = mapLastUpdateTimeToDateTime(searchParams.lastUpdateTime)?.toISOString();
  }
};

const appendTags = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (!isEmpty(searchParams.tags)) {
    body.tags = searchParams.tags;
  }
};

const appendSubscriptionStatus = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.subscriptionStatus && !isEmpty(searchParams.subscriptionStatus)) {
    body.subscriptionStatus = searchParams.subscriptionStatus;
  }
};

const appendProductCodes = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.productCodes && !isEmpty(searchParams.productCodes)) {
    body.productCodes = searchParams.productCodes;
  }
};

const appendConnectionId = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.connectionId) {
    body.connectionId = searchParams.connectionId;
  }
};

const appendOrderNumber = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (searchParams.orderNumber) {
    body.orderNumber = searchParams.orderNumber;
  }
};

const appendMNO = (body: RSimsFiltersRequestBody, searchParams: RSimFilters) => {
  if (!isEmpty(searchParams.mobileNetworkOperatorId)) {
    body.mnoId = searchParams.mobileNetworkOperatorId;
  }

  if (!isEmpty(searchParams.mnoNames)) {
    body.mnoNames = searchParams.mnoNames.map((name) => {
      return name === 'blank' ? null : name;
    });
  }
};

export const prepareRSimsSearchBody = (searchParams: RSimFilters, sort: RSimSort | null = null) => {
  const body: RSimsFiltersRequestBody = {};

  appendIdSerach(body, searchParams.searchText, searchParams.chosenIdType);
  appendSimulatedOutage(body, searchParams);
  appendEidRange(body, searchParams);
  appendLastActiveProfileRange(body, searchParams);
  appendAccounts(body, searchParams);
  appendLastUpdateTime(body, searchParams);
  appendTags(body, searchParams);
  appendSubscriptionStatus(body, searchParams);
  appendProductCodes(body, searchParams);
  appendConnectionId(body, searchParams);
  appendOrderNumber(body, searchParams);
  appendMNO(body, searchParams);

  if (sort) {
    body.order = `${uppercaseFirstLetter(sort.columnName)}:${sort.sort?.toUpperCase()}`;
  }

  return body;
};

export const preparePaginatedRSimsSearchBody = (
  page: number,
  entryPerPage: number,
  searchParams: RSimFilters,
  sort: RSimSort | null = null,
) => {
  const body = prepareRSimsSearchBody(searchParams, sort);
  body.pageNumber = page + 1;
  body.pageSize = entryPerPage;

  return body;
};

export const fetchRSims = async (
  page: number,
  entryPerPage: number,
  filters: RSimFilters,
  sort: RSimSort | null = null,
  signal?: AbortSignal,
) => {
  const body = preparePaginatedRSimsSearchBody(page, entryPerPage, filters, sort);

  const rsimInventoryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_API}`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const rsimProfiles: RSimsDTO = await rsimInventoryResponse.json();

  const assignedTags = await fetchAssignedTagToRSimsProducts({
    euiccs: rsimProfiles.map((rsim) => rsim.euicc),
  });

  const normalizedTagsData = normilizeAssignedTagsToRSim(assignedTags);
  const items = mapRSimsDTO(rsimProfiles, { normalizedTagsData });

  return {
    items,
  };
};

export const fetchRSimsPagination = async (
  filters: RSimFilters,
  sort: RSimSort | null = null,
  signal?: AbortSignal,
) => {
  const body = prepareRSimsSearchBody(filters, sort);

  const rsimInventoryPaginationResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_API}/pagination`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const rsimInventoryPaginationResponseData: RSimInventoryResponsePagination =
    await rsimInventoryPaginationResponse.json();

  return {
    totalNumberOfItems: rsimInventoryPaginationResponseData.totalCount ?? -1,
  };
};

export const fetchRSimSimsProfiles = async (
  euicc: string,
  signal?: AbortSignal,
): Promise<RSimSimProfilesResponseData> => {
  const simInventoryEntryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_SIMS_PROFILE_API}/euicc/${euicc}`,
    { signal },
  );

  const simInventoryResponse: SimProfilesDTO = await simInventoryEntryResponse.json();

  return {
    headers: simInventoryResponse.headers,
    items: simInventoryResponse.items.map((item) => {
      const simInventory = mapSimDTO(item);
      return { ...simInventory, pollingSimStatus: '' };
    }),
  };
};

export type RSimEnability = {
  euicc: string;
  isPrimaryProfileDisabled: boolean;
  isSecondaryProfileDisabled: boolean;
};

export type RSimEnabilities = Record<
  string,
  Pick<RSimEnability, 'isPrimaryProfileDisabled' | 'isSecondaryProfileDisabled'>
>;

export const fetchRSimEnability = async (euiccs: string[], signal?: AbortSignal) => {
  const rSimEnabilityResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_API}/rsim-enability`,
    {
      signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        rSimsEuiccs: euiccs,
      }),
    },
  );

  const rSimEnability: RSimEnability[] = await rSimEnabilityResponse.json();

  return rSimEnability.reduce((acc, value) => {
    const { euicc, ...disabledValues } = value;
    return {
      ...acc,
      [euicc]: disabledValues,
    };
  }, {} as RSimEnabilities);
};

export const setRSimEnability = async (
  euicc: string,
  profileId: 0 | 1,
  enable: boolean,
  signal?: AbortSignal,
) => {
  const response = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_API}/set-rsim-enability`,
    {
      signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        euicc,
        profileId,
        enable,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(response.statusText);
  }
};

export const fetchRSimsLastPollingTimestamps = async (euiccs: string[], signal?: AbortSignal) => {
  const body = {
    ids: {
      type: 1,
      ids: euiccs,
    },
  };
  const rsimInventoryResponse = await authFetch(
    `${envVariables.VITE_SIM_INVENTORY_RSIMS_PROFILE_LAST_POLLING_TIMESTAMP_API}`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const rsimlastPollingTimestamps: RSimsLastPollingTimestampDTO =
    await rsimInventoryResponse.json();

  return rsimlastPollingTimestamps;
};
